<template>
	<transition name="modal">
		<div v-if="show">
			<div
				class="fixed inset-0 z-50 flex items-center justify-center p-4"
				tabindex="-1"
				role="dialog"
				@click="close()"
			>
				<div :class="documentClasses" role="document">
					<!-- Frameless variant -->
					<div
						v-if="frameless"
						:class="framelessPanelClasses"
						@click.stop
					>
						<slot />
					</div>

					<!-- Modern variant -->
					<div
						v-else-if="variant === 'modern'"
						class="w-[600px] max-w-full p-6 flex flex-col gap-4 rounded-[10px] border border-black/10 bg-white shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-4px_rgba(0,0,0,0.10)]"
						:class="panelClass"
						@click.stop
					>
						<!-- Header -->
						<div v-if="title" class="flex flex-col w-full gap-2">
							<div
								class="flex items-start justify-between w-full gap-6"
							>
								<h3
									class="text-[16px] font-medium text-[#303030] leading-[20.8px] tracking-[0.3px] m-0"
								>
									{{ title }}
								</h3>
								<div class="w-10 relative">
									<Button
										icon="times"
										type="tertiary"
										icon-size="md"
										@click="close()"
										class="absolute -top-2.5 -right-2.5"
									></Button>
								</div>
							</div>
							<p
								v-if="subtitle"
								class="text-[14px] font-normal text-[#303030] leading-[21.6px] tracking-[0.3px] m-0"
							>
								{{ subtitle }}
							</p>
						</div>

						<!-- Content -->
						<div
							class="max-h-[70vh]"
							:class="[
								contentClass,
								{ 'overflow-auto': !allowOverflow },
							]"
						>
							<slot></slot>
						</div>

						<!-- Footer -->
						<div
							v-if="!hideFooter"
							class="flex flex-col-reverse w-full gap-4 sm:flex-row sm:justify-end sm:items-center"
						>
							<slot name="footer" :close="close">
								<Button
									v-if="cancelText"
									type="secondary"
									class="w-full sm:w-auto"
									@click="close()"
								>
									{{ cancelText }}
								</Button>
								<Button
									v-if="confirmText"
									ref="confirmButton"
									:type="type || 'primary'"
									class="w-full sm:w-auto"
									:disabled="!allowConfirm"
									@click="close(true)"
								>
									{{ confirmText }}
								</Button>
							</slot>
						</div>
					</div>

					<!-- Default/legacy variant -->
					<div
						v-else
						class="bg-white shadow-2xl rounded-xl"
						:class="panelClass"
						@click.stop
					>
						<div
							v-if="title"
							class="flex items-start justify-between p-6 pb-0"
						>
							<h5
								class="mb-0 text-2xl font-extrabold uppercase font-heading text-neutral-800"
							>
								{{ title }}
							</h5>
							<Button
								icon="times"
								type="tertiary"
								icon-size="lg"
								@click="close()"
							></Button>
						</div>
						<div
							class="px-6 py-5 max-h-[70vh]"
							:class="[
								contentClass,
								{ 'overflow-auto': !allowOverflow },
							]"
						>
							<slot></slot>
						</div>
						<div
							v-if="!hideFooter"
							class="flex flex-col-reverse items-center justify-end gap-3 p-6 pt-0 md:flex-row"
						>
							<slot name="footer" :close="close">
								<Button
									class="w-full"
									v-if="cancelText"
									@click="close()"
									type="secondary"
								>
									{{ cancelText }}
								</Button>
								<Button
									class="w-full"
									v-if="confirmText"
									ref="confirmButton"
									@click="close(true)"
									:type="type || 'primary'"
									:disabled="!allowConfirm"
								>
									{{ confirmText }}
								</Button>
							</slot>
						</div>
					</div>
				</div>
			</div>
			<div class="fixed inset-0 z-40 bg-black bg-opacity-50"></div>
		</div>
	</transition>
</template>

<script>
import Button from '~/components/ui/common/Button'
import { twMerge } from 'tailwind-merge'

export default {
	components: {
		Button,
	},
	props: {
		title: {
			type: String,
			default: null,
		},
		subtitle: {
			type: String,
			default: null,
		},
		type: {
			type: String,
			default: null,
		},
		size: {
			type: String,
			default: 'default',
		},
		variant: {
			type: String,
			default: 'default',
			validator: (value) => ['default', 'modern'].includes(value),
		},
		confirmText: {
			type: String,
			default: 'OK',
		},
		cancelText: {
			type: String,
			default: null,
		},
		allowOverflow: {
			type: Boolean,
			default: false,
		},
		allowConfirm: {
			type: Boolean,
			default: true,
		},
		hideFooter: {
			type: Boolean,
			default: false,
		},
		frameless: {
			type: Boolean,
			default: false,
		},
		panelClass: {
			type: [String, Array, Object],
			default: '',
		},
		documentClass: {
			type: [String, Array, Object],
			default: '',
		},
		contentClass: {
			type: [String, Array, Object],
			default: '',
		},
	},
	data() {
		return {
			show: false,
		}
	},
	computed: {
		documentClasses() {
			return twMerge('w-full', this.modalSizeClasses, this.documentClass)
		},
		framelessPanelClasses() {
			return twMerge(
				'bg-white rounded-lg shadow-2xl overflow-auto max-h-[90vh]',
				this.panelClass,
			)
		},
		modalSizeClasses() {
			const sizeMap = {
				sm: 'max-w-sm',
				default: 'max-w-lg',
				md: 'max-w-2xl',
				lg: 'max-w-4xl',
				xl: 'max-w-6xl',
				'2xl': 'max-w-7xl',
			}
			return sizeMap[this.size] || sizeMap['default']
		},
	},
	mounted() {
		this.show = true
		document.addEventListener('keyup', this.globalKeyDownHandler)

		if (!this.hideFooter && !this.frameless) {
			this.$nextTick(() => this.$refs.confirmButton.focus())
		}
	},
	destroyed() {
		document.removeEventListener('keyup', this.globalKeyDownHandler)
	},
	methods: {
		close(confirm = false) {
			this.show = false

			this.$nextTick(() => {
				if (confirm) {
					this.$emit('confirm')
				} else {
					this.$emit('close')
				}
			})
		},

		globalKeyDownHandler(ev) {
			if (ev.keyCode === 27) {
				ev.preventDefault()
				this.close()
			}
		},
	},
}
</script>
